import { ArrowRight01Icon } from '@hugeicons/react'
import clsx from 'clsx'

import {
  COMPLETED_ACCENT,
  OBJECTIVES_FEATURE_COPY,
  getPrimaryResourceType,
  resolveResourceType
} from '@/shared/resourceModuleParity'
import { Button } from '@/shared/ui/Button'
import { GameIcon } from '@/shared/ui/GameIcon'
import { MobileSheet } from '@/shared/ui/MobileSheet'
import { ProgressBar, type ProgressBarVariant } from '@/shared/ui/ProgressBar'
import { ResourceModuleIcon } from '@/shared/ui/ResourceModuleIcon'
import { useNavigation } from '@/shared/hooks/useNavigation'
import type {
  ConditionProgress,
  ObjectiveCondition,
  ObjectiveWithProgress
} from '@/types/objective'

import {
  getActionLabel,
  getConditionInstruction
} from '../model/resourceCopy'
import type { Goal } from '../model/milestones'
import { MilestoneDetailContent } from './MilestoneDetailContent'

type ConditionCardProps = {
  condition: ObjectiveCondition
  progress: ConditionProgress
  index: number
  isObjectiveCompleted: boolean
  onNavigate: (route: string) => void
}

const ConditionCard = ({
  condition,
  progress,
  index,
  isObjectiveCompleted,
  onNavigate
}: ConditionCardProps) => {
  const resolved = resolveResourceType(condition.resource_type)
  const { satisfied, count, required } = progress
  const cappedCount = Math.min(count, required)
  const remaining = required - cappedCount
  const percent = required > 0 ? Math.round((cappedCount / required) * 100) : 0
  const pVariant: ProgressBarVariant = satisfied
    ? 'success'
    : count > 0
      ? 'progress'
      : 'primary'
  const accent = satisfied ? COMPLETED_ACCENT : resolved.accentColor

  return (
    <div
      className={clsx(
        'overflow-hidden rounded-lg border',
        satisfied ? 'border-green-200 bg-green-50' : 'border-gray-100 bg-white'
      )}
    >
      <div className="flex items-start gap-3 p-3">
        <div className="relative shrink-0">
          <ResourceModuleIcon
            resourceType={condition.resource_type}
            size={20}
            tileSize={44}
            completed={satisfied}
          />
          <div
            className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full text-2xs font-black text-white"
            style={{ backgroundColor: accent }}
          >
            {index + 1}
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <p
            className={clsx(
              'm-0 text-sm font-bold leading-snug',
              satisfied ? 'text-green-800' : 'text-gray-900'
            )}
          >
            {getActionLabel(condition)}
          </p>
          <p
            className={clsx(
              'm-0 mt-0.5 text-xs leading-relaxed',
              satisfied ? 'text-green-700' : 'text-gray-700'
            )}
          >
            {getConditionInstruction(condition)}
          </p>
        </div>

        {satisfied && (
          <div className="shrink-0 rounded-full bg-green-100 px-2 py-0.5">
            <span className="text-2xs font-black text-green-700">✓ Feito</span>
          </div>
        )}
      </div>

      {!satisfied && (
        <div className="border-t border-gray-100 px-3 pb-3 pt-2.5">
          <div className="mb-2.5 flex items-center gap-2">
            <div className="flex items-baseline gap-1">
              <span
                className="text-2xs font-bold leading-none"
                style={{ color: accent }}
              >
                {cappedCount}
              </span>
              <span className="text-2xs leading-none text-gray-700">/ {required}</span>
            </div>
            <ProgressBar
              value={percent}
              variant={pVariant}
              size="sm"
              className="flex-1 self-center"
            />
            {remaining > 0 && (
              <span className="shrink-0 text-2xs font-semibold text-gray-700">
                {remaining}{' '}
                {remaining === 1 ? resolved.label.singular : resolved.label.plural}{' '}
                restante{remaining === 1 ? '' : 's'}
              </span>
            )}
          </div>

          {!isObjectiveCompleted && (
            <Button
              variant="primary"
              size="sm"
              className="w-full"
              trailingIcon={<ArrowRight01Icon size={14} variant="stroke" aria-hidden />}
              onClick={() => onNavigate(resolved.route)}
            >
              Ir para {resolved.section}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

type Props = {
  /** Meta selecionada — escada de marcos ou objetivo solto. */
  goal: Goal | null
  onClose: () => void
}

/**
 * Estado expandido de uma meta.
 *
 * É o único estado expandido que existe: o card não expande in-place, para não
 * criar um terceiro estado de UI duplicando um padrão de navegação que o sheet
 * já resolve.
 */
export const ObjectiveDetailSheet = ({ goal, onClose }: Props) => {
  const { navigateTo } = useNavigation()

  if (!goal) return null

  function handleNavigateFromLadder(route: string) {
    onClose()
    setTimeout(() => navigateTo(route), 180)
  }

  if (goal.kind === 'ladder') {
    const sampleCondition = goal.milestones[0]?.objective_conditions?.[0]
    const ladderResolved = resolveResourceType(sampleCondition?.resource_type)

    return (
      <MobileSheet
        open
        onClose={onClose}
        ariaLabel={goal.group.name}
        footer={
          <Button
            variant="primary"
            size="md"
            className="w-full"
            trailingIcon={<ArrowRight01Icon size={16} variant="stroke" aria-hidden />}
            onClick={() => handleNavigateFromLadder(ladderResolved.route)}
          >
            Ir para {ladderResolved.section}
          </Button>
        }
      >
        <MilestoneDetailContent goal={goal} />
      </MobileSheet>
    )
  }

  const objective = goal.objective

  const {
    name,
    description,
    coin_reward,
    completed,
    objective_conditions = [],
    conditions_progress = []
  } = objective

  const isCompleted = completed
  const primaryType = getPrimaryResourceType(objective_conditions)
  const resolved = resolveResourceType(primaryType)
  const iconBg = isCompleted ? COMPLETED_ACCENT : resolved.accentColor

  const sumCurrent = conditions_progress.reduce(
    (acc, cp) => acc + Math.min(cp.count, cp.required),
    0
  )
  const sumTarget = conditions_progress.reduce((acc, cp) => acc + cp.required, 0)
  const overallPercent =
    sumTarget > 0 ? Math.round((sumCurrent / sumTarget) * 100) : 0

  const pVariant: ProgressBarVariant = isCompleted
    ? 'success'
    : overallPercent > 0
      ? 'progress'
      : 'primary'

  function handleNavigate(route: string) {
    onClose()
    setTimeout(() => navigateTo(route), 180)
  }

  return (
    <MobileSheet open onClose={onClose} ariaLabel={name}>
      <div
        className="mb-4 rounded-xl p-4"
        style={{
          background: `linear-gradient(135deg, ${iconBg}18 0%, ${iconBg}08 100%)`,
          border: `1px solid ${iconBg}25`
        }}
      >
        <div className="flex items-start gap-3">
          <ResourceModuleIcon
            resourceType={primaryType}
            size={28}
            tileSize={56}
            completed={isCompleted}
          />
          <div className="min-w-0 flex-1 pt-0.5">
            <p className="m-0 text-caps font-bold uppercase tracking-[0.08em] text-gray-700">
              {OBJECTIVES_FEATURE_COPY.singular}
            </p>
            <h3 className="m-0 mt-0.5 text-base font-bold leading-snug text-gray-900">
              {name}
            </h3>
          </div>
        </div>

        {coin_reward > 0 && (
          <div
            className="mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5"
            style={{ backgroundColor: `${iconBg}20` }}
          >
            <GameIcon name="coins" size={14} />
            <span className="text-xs font-bold" style={{ color: iconBg }}>
              Complete e ganhe {coin_reward.toLocaleString('pt-BR')} moedas
            </span>
          </div>
        )}

        <div className="mt-3">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-2xs text-gray-700">
              {isCompleted
                ? OBJECTIVES_FEATURE_COPY.completedLabel({ exclamation: true })
                : `${sumCurrent} de ${sumTarget} tarefas concluídas`}
            </span>
            <span
              className="text-xs font-bold"
              style={{ color: isCompleted ? COMPLETED_ACCENT : iconBg }}
            >
              {isCompleted ? '100%' : `${overallPercent}%`}
            </span>
          </div>
          <ProgressBar
            value={isCompleted ? 100 : overallPercent}
            variant={pVariant}
            size="sm"
          />
        </div>
      </div>

      {description && (
        <p className="mb-4 text-sm leading-relaxed text-gray-700">{description}</p>
      )}

      {objective_conditions.length > 0 ? (
        <>
          <div className="mb-3 flex items-center gap-2">
            <span className="text-caps font-bold uppercase tracking-[0.08em] text-gray-700">
              Como completar
            </span>
            <div className="flex-1 border-t border-gray-100" />
            <span className="text-2xs text-gray-700">
              {objective_conditions.length}{' '}
              {objective_conditions.length === 1 ? 'tarefa' : 'tarefas'}
            </span>
          </div>

          <div className="flex flex-col gap-3 pb-2">
            {objective_conditions.map((cond, i) => {
              const progress = conditions_progress.find(
                cp => cp.condition_id === cond.id
              ) ?? {
                condition_id: cond.id,
                satisfied: false,
                count: 0,
                required: cond.quantity
              }
              return (
                <ConditionCard
                  key={cond.id}
                  condition={cond}
                  progress={progress}
                  index={i}
                  isObjectiveCompleted={isCompleted}
                  onNavigate={handleNavigate}
                />
              )
            })}
          </div>
        </>
      ) : null}
    </MobileSheet>
  )
}
